import Empty from "@/components/Empty";
import LoadMore from "@/components/LoadMore";
import RpIcon from "@/components/RpIcon";
import TransItem from "@/components/skeleton/TransItem";
import API from "@/constant/API";
import RouteConstant from "@/constant/RouteConstant";
import useLoadMore from "@/hook/useLoadMore";
import useRenders from "@/hook/useRenders";
import SearchItem from "@/pages/components/SearchItem";
import axiosInstance from "@/utils/axios";
import { Box, Stack } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CardTransItem from "./CardTransItem";
import SimpleHeader from "@/pages/components/Headers/SimpleHeader";
import useScrollList from "@/hook/useScrollList";
import { useInfiniteScroll } from "ahooks";
import {orderBy} from "es-toolkit/array";
import useRestoreScroll from '@/hook/useRestoreScroll';
import {useKeepAliveContext} from "@hhhgamehhh/keepalive-for-react";

export interface CardTransItemType {
  transactionId: string,
  refundTransactionAmount: null | string,
  cardId: string,
  billAmount: string,
  billCurrency: number,
  oriBillAmount: string,
  clearAmount: string,
  clearTime: number,
  reversalTotalBillAmount: null | string,
  reversalBillCurrency: null,
  reversalLastTime: null,
  refundAmount: null,
  refundTime: null,
  transactionAmount: string,
  transactionCurrency: number,
  conversionRate: string,
  channel: string,
  fxFees: string,
  atmFees: string,
  wallet?: null,
  clearedAt?: null,
  status: string,
  isRefund: number,
  hasRefund: number,
  mccCode: number,
  merchantId: string,
  merchantName: string,
  merchantCity: string,
  merchantPostCode?: null | number,
  merchantState: null | number,
  merchantCountry: string,
  mccCategory: "Computer Network Services",
  responseCode?: null,
  declineReason?: null,
  label?: null,
  transactionDateTime?: null,
  createTime: number,
  billCurrencyCode: string,
  transactionCurrencyCode: string,
  mcc: number,
  last4: number,
  refundStatus?: null,
  cardAuthorizations: string[],
  refundOrders: any[],
  payVersion: number
}

export default function CardTransactions({cardId, pageable, searchAll = false, cacheScrollList = false,
                                           pageTitle = false, hideTitle = false, disableScrollControl = false, searchParams, cardType}) {
  const [data, setData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const {t} = useTranslation();
  const navigate = useNavigate();

  const {renderDate} = useRenders();

  const [search, setSearch] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(pageable ? 20 : 10);
  const [searchValues, setSearchValues] = useState(searchParams ?? {});

  const handleSearch = (values) => {
    if (values.daysLasting === 'customize') {
      const startTime = renderDate(values.startTime)
      const endTime = renderDate(values.endTime)
      delete values.daysLasting;
      values = {...values, startTime, endTime};
    } else if (values.daysLasting === 'all') {
      values = {};
    } else {
      delete values.startTime;
      delete values.endTime;
    }
    setSearchValues(values);
    setSearch(false)
  }

  const ref = useRef(null);
  useRestoreScroll(ref, 0);

  const { refresh, destroy } = useKeepAliveContext();
  const getList = async (cardId, pageNum: number, pageSize: number, values: any) => {
    const res = await axiosInstance.post(API.CARD_TRANS_LIST, {
      pageNum, pageSize,
      cardId: cardId,
      ...values,
    });

    const result = {
      list: orderBy(res?.list, ['createTime'], 'desc') || [],
      total: res?.total || 0,
      page: pageNum,
    };
    return result;
  }

  const getCardTransListReq = useInfiniteScroll((d) => {
    const pageNum = d ? d.page + 1 : 1
    return getList(cardId, pageNum, pageSize, searchValues);
  }, {
    manual: false,
    target: ref,
    threshold: 100,
    isNoMore: (d) => {
      // d?.total <= d.list.length,
      return (d && d.list.length >= d.total)
    }
  });

  const _toTransList = () => {
    if (pageable) {
      setSearch(true)
      return;
    }
    navigate(`${RouteConstant.CARD_TRANS_LIST}?cardId=${cardId}`);
  }

  const _onBackClick = () => {
    destroy()
    navigate(-1);
    // clearScrollList();
  }

  // if (!searchAll && !cardId) return null;

  return (
    <Stack direction="column"
    sx={{
      height: '100%',
      flex: 1,
    }}
    >
      {pageTitle && <SimpleHeader onBackClick={_onBackClick} title={t("transactions")} />}

      <Stack direction="column" style={{ width: '100%', flex: 1, height: '100%', overflow: 'hidden' }}>
        {(pageTitle || hideTitle) ? null : <Stack direction={'row'} gap={'4px'} sx={{marginBottom:'10px'}} justifyContent={'space-between'}>
          <span className={'h4'}>{t("transactions")}</span>
          <Stack direction={'row'} alignItems={'center'} className={'small'} onClick={_toTransList}>
            <span>{t('more')}</span>
            <RpIcon icon={pageable ? 'filter' : 'arrow-right-simple'} size={16} clickable/>
          </Stack>
        </Stack>}

        <div
          style={{
            height: '100%', // '500px',
            flex: 1,
            overflow: 'auto',
            padding: pageTitle ? '0 16px' : '',
            // paddingBottom: '24px'
          }}
          className={`flex-1 ${pageTitle || hideTitle ? '' : 'card-box'}`} ref={ref}>
          {getCardTransListReq.loading ? (
            <>
              <TransItem/>
              <TransItem/>
              <TransItem/>
            </>
          ) : getCardTransListReq?.data?.list.length === 0 ? (
            <Box>
              <Empty text={t('no_data')}/>
            </Box>
          ) : (
            <Box>
              {getCardTransListReq?.data?.list.map((item, idx) => (
                <div key={idx} className="cursor-pointer border-b border-solid border-b-border">
                  <CardTransItem record={item} cardType={cardType} />
                </div>
              ))}
            </Box>
          )}
          {pageable && !getCardTransListReq.noMore &&
            <LoadMore disabled={getCardTransListReq.loadingMore} loading={getCardTransListReq.loadingMore} hasMore={!getCardTransListReq.noMore} onClick={() => {
              getCardTransListReq.loadMore()
            }}/>
          }
        </div>
      </Stack>

      <SearchItem open={search} onClose={() => setSearch(false)} onSearch={handleSearch}></SearchItem>
    </Stack>
  );
}
